import {isVolumeUnit} from 'inventory/InventoryUnitUtil';
import {
  getLotConcentrationStatus,
  LotConcentrationStatus,
} from 'notebook/integrations/chemistry/custom-evals/LotPurityConcentrationCustomEval';
import {
  DEFAULT_MASS_UNIT_SYMBOL,
  DEFAULT_VOLUME_UNIT_SYMBOL,
  resolveUnitsFromTypedValues,
} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometryUnits';
import {getDefaultUnitCustomEval} from 'notebook/integrations/chemistry/custom-evals/StoichiometryUnitDefaultsCustomEval';
import {StoichiometryCustomEvalOptions} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {UnitReferenceColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEval} from 'notebook/tables/constants/CustomEvalConstants';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {isTypedValueEmpty, isUnitReferenceTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ResolveQueryFn} from 'notebook/tables/typed-values/TypedValueResolution';

export const AMOUNT_UNIT_MASS_ERROR =
  'Lot has a defined concentration. Amount unit must be a volume unit (e.g., mL, L).';

const defaultMassUnitCustomEval =
  getDefaultUnitCustomEval<StoichiometryCustomEvalOptions>(DEFAULT_MASS_UNIT_SYMBOL);
const defaultVolumeUnitCustomEval =
  getDefaultUnitCustomEval<StoichiometryCustomEvalOptions>(DEFAULT_VOLUME_UNIT_SYMBOL);

/** Returns a ValueError if the unit resolves to a non-volume unit; null if volume or still loading. */
function getNonVolumeUnitError(
  unitTypedValue: TypedValue,
  resolveFromResolutionQuery: ResolveQueryFn
): ExcessUtil.ValueError | null {
  if (!isUnitReferenceTypedValue(unitTypedValue)) {
    return null;
  }
  const {units, error} = resolveUnitsFromTypedValues([unitTypedValue], resolveFromResolutionQuery);
  if (error != null || units == null) {
    return null;
  }
  const unit = units.find((u) => u.id === unitTypedValue.unitSummary.id);
  if (unit == null || isVolumeUnit(unit)) {
    return null;
  }
  return new ExcessUtil.ValueError(AMOUNT_UNIT_MASS_ERROR);
}

/**
 * Custom eval for the Amount unit column.
 *
 * cellDependencies layout (from getCustomEvalDependencies):
 *   [0] = CHEMISTRY_LOT (lot BIOENTITY)
 *
 * Behavior:
 * - No lot, or lot without a concentration → empty cell defaults to mass (g)
 * - Lot with a concentration + empty cell → defaults to volume (L)
 * - Lot with a concentration + non-volume unit in the cell (including text/formula input) → keep the value,
 *   show a ValueError
 * - Lot loading → loading error
 */
const stoichiometryAmountUnitCustomEval: CustomEval<StoichiometryCustomEvalOptions> = (
  typedValue: TypedValue,
  columnDefinition: UnitReferenceColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: StoichiometryCustomEvalOptions,
  resolveFromResolutionQuery: ResolveQueryFn,
  resolveTypedValueToType
) => {
  const lotConcentrationStatus = getLotConcentrationStatus(
    cellDependencies[0] ?? null,
    resolveFromResolutionQuery
  );
  if (lotConcentrationStatus === LotConcentrationStatus.LOADING) {
    return {val: typedValue, error: new ExcessUtil.LoadingError(''), locked: false};
  }

  if (lotConcentrationStatus === LotConcentrationStatus.NO_CONCENTRATION) {
    return defaultMassUnitCustomEval(
      typedValue,
      columnDefinition,
      cellDependencies,
      customEvalOptions,
      resolveFromResolutionQuery,
      resolveTypedValueToType
    );
  }

  if (isTypedValueEmpty(typedValue)) {
    return defaultVolumeUnitCustomEval(
      typedValue,
      columnDefinition,
      cellDependencies,
      customEvalOptions,
      resolveFromResolutionQuery,
      resolveTypedValueToType
    );
  }

  // Custom evals run before typed value resolution, so text or formula input (e.g. a pasted "g") hasn't been
  // resolved to a unit reference yet.
  const {resolvedValue, error: resolutionError} = resolveTypedValueToType(columnDefinition, typedValue);
  if (resolutionError != null || resolvedValue == null) {
    return {val: resolvedValue ?? typedValue, error: resolutionError, locked: false};
  }
  const error = getNonVolumeUnitError(resolvedValue, resolveFromResolutionQuery);
  return {val: resolvedValue, error, locked: false};
};

export default stoichiometryAmountUnitCustomEval;
